import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authAPI } from '@services/index';
import secureStorage from '@/utils/secureStorage';

const initialState = {
  // Check both in-memory token AND stored session
  isAuthenticated: authAPI.isAuthenticated(),
  hasSession: authAPI.hasSession(), // For "remember me" - user has refresh token
  user: secureStorage.getUser(), // Try to restore user from storage
  token: authAPI.getToken(),
  refreshToken: authAPI.getRefreshToken(),
  accessControl: secureStorage.getAccessControl(), // Restore access control from storage
  loading: false,
  sessionRestoring: false, // Track session restoration state
  error: null,
};

export const login = createAsyncThunk('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const response = await authAPI.login(credentials);
    return response;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Login failed');
  }
});

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await authAPI.getCurrentUser();
      // Also save user to storage
      secureStorage.setUser(user);
      return user;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user');
    }
  }
);

/**
 * Restore session from refresh token
 * Called on app startup when user has "remember me" enabled
 */
export const restoreSession = createAsyncThunk(
  'auth/restoreSession',
  async (_, { rejectWithValue }) => {
    try {
      const refreshToken = authAPI.getRefreshToken();
      if (!refreshToken) {
        return rejectWithValue('No refresh token');
      }

      // Refresh the access token
      const response = await authAPI.refreshToken(refreshToken);

      if (response.access) {
        secureStorage.setAccessToken(response.access);
        return { access: response.access };
      }

      return rejectWithValue('Failed to refresh token');
    } catch (error) {
      // Clear invalid session data
      secureStorage.clearAuthData();
      return rejectWithValue(error.response?.data?.message || 'Session expired');
    }
  }
);

/**
 * Fetch current user's access control permissions
 * Called on app startup or when permissions need to be refreshed
 */
export const getAccessControl = createAsyncThunk(
  'auth/getAccessControl',
  async (_, { rejectWithValue }) => {
    try {
      const accessControl = await authAPI.getAccessControl();
      return accessControl;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch access control');
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  await authAPI.logout();
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.hasSession = true;
        state.user = action.payload.user;
        state.token = action.payload.access;
        state.refreshToken = action.payload.refresh;
        state.accessControl = action.payload.access_control || null;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get current user
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(getCurrentUser.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
      })
      // Restore session
      .addCase(restoreSession.pending, (state) => {
        state.sessionRestoring = true;
        state.error = null;
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.sessionRestoring = false;
        state.isAuthenticated = true;
        state.token = action.payload.access;
        state.error = null;
      })
      .addCase(restoreSession.rejected, (state) => {
        state.sessionRestoring = false;
        state.isAuthenticated = false;
        state.hasSession = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
      })
      // Get access control
      .addCase(getAccessControl.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAccessControl.fulfilled, (state, action) => {
        state.loading = false;
        state.accessControl = action.payload;
      })
      .addCase(getAccessControl.rejected, (state) => {
        state.loading = false;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.hasSession = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.accessControl = null;
      });
  },
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;
